import { useMemo, useEffect, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type FilterFn,
  type ColumnFiltersState,
  type Row,
  type VisibilityState,
} from '@tanstack/react-table';
import { type Settings, type ViewerBoardInfo, type ViewerBoardItem, type ViewerBoardField, type ViewerAsset } from './types';
import { Events } from './Events';
import { Grid } from './Grid';
import { Table } from './Table';
import { Value, parseDateTime } from './Value';
import { DebouncedInput } from './DebouncedInput';
import { Pagination } from './Pagination';
import { useViewer } from './hooks/use-viewer';
import { settingsToCSSVariables, getLabel } from './utils';

const GRID_ITEMS_PER_LOAD = 20;
const EVENTS_ITEMS_PER_LOAD = 10;
const TABLE_ITEMS_PER_PAGE_DEFAULT = 10;

type AssetsById = Record<string, ViewerAsset>;

export function getValue(row: ViewerBoardItem, field: ViewerBoardField, settings: Settings) {
  if (field.type === 'group') {
    return row.group?.title;
  }

  if (field.type === 'name') {
    return row.name;
  }

  if (field.type === 'file' || field.type === 'link' || field.type === 'email' || field.type === 'status') {
    return row.values[field.id]?.value;
  }

  if (field.type === 'mirror') {
    const columnSettings = settings.columnSettings[field.id] || {};
    const summarizeNumbers = !!columnSettings.summarizeNumbers;

    if (summarizeNumbers) {
      const text = (row.values[field.id]?.value as any)?.text;

      if (!text) {
        return null;
      }

      try {
        const numbers = text.split(', ').flatMap((n: string) => (n ? [parseFloat(n.trim())] : []));
        const sum = numbers.reduce((acc: number, n: number) => acc + n, 0);

        return sum;
      } catch (err) {}
    }
  }

  return (row.values[field.id]?.value as { text: string })?.text;
}

const NON_ASCII = /[^\u0000-\u007f]/;
const DIACRITICS = /\p{Diacritic}/gu;
const WORD_CHARACTER = /[\p{L}\p{N}]/u;

// lowercased and stripped of accents, so that a visitor searching "Muller" still finds "Müller".
// the accent pass is skipped for plain ASCII values, which is almost all of them.
function normalizeForSearch(rawValue: unknown) {
  const value = rawValue != null && typeof rawValue === 'object' && 'text' in rawValue ? (rawValue as { text: string }).text : rawValue;

  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  const lowercased = String(value).toLowerCase();

  return NON_ASCII.test(lowercased) ? lowercased.normalize('NFD').replace(DIACRITICS, '') : lowercased;
}

// a term has to start a word to count as a match: "NJ" finds "Newark, NJ" but not "enjoy".
// anything that isn't a letter or a digit opens a new word, so hyphens, commas, slashes and
// brackets all still work as separators.
function containsAtWordStart(haystack: string, term: string) {
  let index = haystack.indexOf(term);

  while (index !== -1) {
    if (index === 0 || !WORD_CHARACTER.test(haystack[index - 1])) {
      return true;
    }

    index = haystack.indexOf(term, index + 1);
  }

  return false;
}

const wordStartFilter: FilterFn<any> = (row, columnId, value) => {
  const terms = normalizeForSearch(value).split(/\s+/).filter(Boolean);

  if (terms.length === 0) {
    return true;
  }

  const searchValue = normalizeForSearch(row.getValue(columnId));

  return terms.every((term) => containsAtWordStart(searchValue, term));
};

type StatusValue = { text: string | null; index: number | null } | undefined;
type DropdownValue = { text: string | null; ids: number[] | null } | undefined;
type FilterOption = { value: string; label: string };

const statusFilter: FilterFn<any> = (row, columnId, filterValue) => {
  return (row.getValue(columnId) as StatusValue)?.text === filterValue;
};

// the accessor turns a dropdown into the joined text of its labels, and a label name can itself
// contain a comma, so filtering reads the raw value's label ids rather than splitting that text.
function getDropdownValue(row: Row<ViewerBoardItem>, columnId: string) {
  return row.original.values[columnId]?.value as DropdownValue;
}

// a dropdown cell carries several labels, so a row matches when the picked one is among them.
const dropdownFilter: FilterFn<any> = (row, columnId, filterValue) => {
  return !!getDropdownValue(row, columnId)?.ids?.some((id) => String(id) === filterValue);
};

// only status and dropdown columns can be filters: they have a fixed set of labels to offer as
// options, where text columns would list every typo as its own.
function isFilterColumn(field: ViewerBoardField, settings: Settings) {
  return (field.type === 'status' || field.type === 'dropdown') && !!settings.columnSettings?.[field.id]?.showAsFilter;
}

function getFilterFn(field: ViewerBoardField): FilterFn<any> {
  return field.type === 'dropdown' ? dropdownFilter : statusFilter;
}

function getFilterOptions(field: ViewerBoardField, rows: Row<ViewerBoardItem>[], selected: string): FilterOption[] {
  return field.type === 'dropdown' ? getDropdownFilterOptions(field, rows, selected) : getStatusFilterOptions(field, rows, selected);
}

function isDisplayedColumn(field: ViewerBoardField, settings: Settings) {
  if (
    (settings.mode === 'grid' && settings.gridItemHeadlineColumn === field.id) ||
    (settings.mode === 'events' && settings.eventsItemHeadlineColumn === field.id)
  ) {
    return true;
  }

  return !settings.columnSettings?.[field.id]?.hidden;
}

// the labels carried by the rows that match everything else (search and the other filters), so a
// visitor is never offered a label that leaves nothing to show. they're ordered as on the board.
function getStatusFilterOptions(field: ViewerBoardField, rows: Row<ViewerBoardItem>[], selected: string) {
  const indexByLabel = new Map<string, number | null>();

  for (const row of rows) {
    const value = row.getValue(field.id) as StatusValue;

    if (value?.text && !indexByLabel.has(value.text)) {
      indexByLabel.set(value.text, value.index);
    }
  }

  // a selected label has to stay listed even when a search leaves no row carrying it, or the select
  // would silently show a different option than the one filtering
  if (selected && !indexByLabel.has(selected)) {
    indexByLabel.set(selected, null);
  }

  const positions = (field.settings.labels_positions_v2 || {}) as Record<string, number>;
  const position = (index: number | null) => (index === null ? Number.MAX_SAFE_INTEGER : (positions[index] ?? index));

  return [...indexByLabel.entries()].sort(([, a], [, b]) => position(a) - position(b)).map(([label]) => ({ value: label, label }));
}

// the same, for a dropdown column: its settings already list the labels in board order, so the
// options are that list narrowed to the labels the matching rows carry.
function getDropdownFilterOptions(field: ViewerBoardField, rows: Row<ViewerBoardItem>[], selected: string) {
  const carried = new Set<string>();

  for (const row of rows) {
    for (const id of getDropdownValue(row, field.id)?.ids || []) {
      carried.add(String(id));
    }
  }

  const labels = (field.settings.labels || []) as { id: number; name: string }[];

  // a selected label stays listed even when a search leaves no row carrying it, as for a status.
  // a label deleted on the board is never offered, even while items still carry its id: its
  // settings entry, and with it the name to show, is gone.
  return labels
    .filter((label) => carried.has(String(label.id)) || String(label.id) === selected)
    .map((label) => ({ value: String(label.id), label: label.name }));
}

const columnHelper = createColumnHelper<ViewerBoardItem>();

function getSortColumn(settings: Settings, boardInfo: ViewerBoardInfo) {
  if (settings.mode === 'events') {
    if (settings.eventsItemDateColumn && boardInfo.fields.find((f) => f.id === settings.eventsItemDateColumn)) {
      return settings.eventsItemDateColumn;
    }

    return 'name';
  }

  return settings.sortColumn && boardInfo.fields.find((f) => f.id === settings.sortColumn) ? settings.sortColumn : 'name';
}

type ViewerProps = {
  settings: Settings;
  boardInfo: ViewerBoardInfo;
  boardItems: ViewerBoardItem[];
  assets: ViewerAsset[];
  widgetId: string;
  mode: 'preview' | 'publish';
  previewTumbnailGeneratorUrl?: string;
  styles?: string;
};

export function Viewer({ settings, boardInfo, boardItems, mode, previewTumbnailGeneratorUrl, assets, widgetId, styles }: ViewerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const noBoardItems = boardItems.length === 0;

  const assetsById = useMemo(() => {
    return assets?.reduce((prev, curr) => {
      return { ...prev, [curr.id]: curr };
    }, {} as AssetsById);
  }, [assets]);

  const filterFields = boardInfo.fields.filter((field) => isFilterColumn(field, settings));

  // a hidden column can still be a filter. it has to stay a table column to be filtered on, so it's
  // hidden through column visibility instead, which every layout's getVisibleCells respects.
  const columnVisibility: VisibilityState = Object.fromEntries(
    filterFields.filter((field) => !isDisplayedColumn(field, settings)).map((field) => [field.id, false]),
  );

  const columns = boardInfo.fields
    .filter((field) => isDisplayedColumn(field, settings) || isFilterColumn(field, settings))
    .map((field) => {
      let customSortFn = null;

      if (field.type === 'file' || field.type === 'link' || field.type === 'email' || field.type === 'status') {
        customSortFn = (rowA: any, rowB: any) => {
          let statusA = rowA.getValue(field.id)?.text;
          let statusB = rowB.getValue(field.id)?.text;

          if (typeof statusA !== 'string') {
            statusA = '';
          }

          if (typeof statusB !== 'string') {
            statusB = '';
          }

          return statusA.localeCompare(statusB);
        };
      }

      return columnHelper.accessor((row) => getValue(row, field, settings), {
        id: field.id,

        cell: (cell) => {
          const value = cell.getValue();
          const columnSettings = settings?.columnSettings || {};
          const fieldSettings = columnSettings[field.id] || {};

          if (fieldSettings && fieldSettings.hidden) {
            return null;
          }

          return (
            <Value
              field={field}
              value={value}
              settings={settings}
              mode={mode}
              widgetId={widgetId}
              assetsById={assetsById || {}}
              previewTumbnailGeneratorUrl={previewTumbnailGeneratorUrl}
              columnSettings={columnSettings}
            />
          );
        },
        header: () => field.label,

        ...(customSortFn ? { sortingFn: customSortFn } : {}),

        filterFn:
          settings.mode === 'events' && settings.eventsItemDateColumn && field.id === settings.eventsItemDateColumn
            ? (row, columnId, filterValue) => {
                const value = row.getValue(columnId);

                if (!value || typeof value !== 'string') {
                  return true;
                }

                const { year, month, day } = parseDateTime(value);
                const eventDate = new Date(`${year}-${month}-${day}`);
                const today = new Date();

                eventDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);

                if (filterValue === 'upcoming') {
                  return eventDate >= today;
                } else {
                  return eventDate < today;
                }
              }
            : isFilterColumn(field, settings)
              ? getFilterFn(field)
              : undefined,
      });
    });

  const table = useReactTable({
    data: boardItems,
    columns,
    state: {
      globalFilter: searchTerm,
      columnFilters,
      columnVisibility,
    },
    globalFilterFn: wordStartFilter,
    // a column only kept around to be filtered on stays out of search, like any other hidden column
    getColumnCanGlobalFilter: (column) => column.getIsVisible() && !settings.columnSettings?.[column.id]?.excludeFromSearch,
    onGlobalFilterChange: setSearchTerm,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const filterFieldIds = filterFields.map((field) => field.id).join(',');

  // a column that stops being a filter (toggled off in the editor) would otherwise keep filtering,
  // with no select left to clear it
  useEffect(() => {
    setColumnFilters((filters) => {
      const kept = filters.filter((filter) => filter.id === settings.eventsItemDateColumn || filterFieldIds.split(',').includes(filter.id));

      return kept.length === filters.length ? filters : kept;
    });
  }, [filterFieldIds]);

  const eventsDateColumn =
    settings.mode === 'events' &&
    settings.eventsItemDateColumn &&
    boardInfo.fields.find((f) => f.id === settings.eventsItemDateColumn) &&
    table.getColumn(settings.eventsItemDateColumn);

  const { setCSSVariables } = useViewer();

  useEffect(() => {
    if (!settings?.mode) {
      return;
    }

    // initial filter settings per mode
    if (settings?.mode === 'grid') {
      table.setPageSize(settings.gridItemsPerPage || GRID_ITEMS_PER_LOAD);
      setSearchTerm('');
      setColumnFilters([]);

      table.setSorting([
        {
          id: getSortColumn(settings, boardInfo),
          desc: settings.sortDirection === 'desc',
        },
      ]);
    } else if (settings?.mode === 'events') {
      table.setPageSize(EVENTS_ITEMS_PER_LOAD);
      setSearchTerm('');

      if (eventsDateColumn && settings.eventsItemDateColumn) {
        setColumnFilters([
          {
            id: settings.eventsItemDateColumn,
            value: 'upcoming',
          },
        ]);

        table.setSorting([
          {
            id: settings.eventsItemDateColumn!,
            desc: false,
          },
        ]);
      }
    } else if (settings?.mode === 'table') {
      table.setPageSize(settings.tableDefaultItemsPerPage || TABLE_ITEMS_PER_PAGE_DEFAULT);
      setSearchTerm('');
      setColumnFilters([]);

      table.setSorting([
        {
          id: getSortColumn(settings, boardInfo),
          desc: settings.sortDirection === 'desc',
        },
      ]);
    }
  }, [settings?.mode]);

  useEffect(() => {
    if (settings.mode !== 'events') {
      table.setSorting([
        {
          id: getSortColumn(settings, boardInfo),
          desc: settings.sortDirection === 'desc',
        },
      ]);
    } else if (eventsDateColumn && settings.eventsItemDateColumn) {
      setColumnFilters([
        {
          id: settings.eventsItemDateColumn,
          value: 'upcoming',
        },
      ]);

      table.setSorting([
        {
          id: settings.eventsItemDateColumn,
          desc: false,
        },
      ]);
    }
  }, [settings.sortColumn, settings.sortDirection, settings.eventsItemDateColumn]);

  useEffect(() => {
    if (settings.mode === 'grid') {
      table.setPageSize(settings.gridItemsPerPage || GRID_ITEMS_PER_LOAD);
    } else if (settings.mode === 'table') {
      table.setPageSize(settings.tableDefaultItemsPerPage || TABLE_ITEMS_PER_PAGE_DEFAULT);
    }
  }, [settings.tableDefaultItemsPerPage, settings.gridItemsPerPage]);

  useEffect(() => {
    if (!settings) {
      return;
    }

    setCSSVariables(settingsToCSSVariables(settings));
  }, [settings]);

  const currentPage = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  return (
    <>
      {styles ? <style dangerouslySetInnerHTML={{ __html: styles }} /> : null}
      <div className="Viewer">
        {settings.search || settings.headline || settings.mode === 'events' || filterFields.length > 0 ? (
          <div
            className={
              settings.mode === 'table'
                ? 'WidgetHeader WidgetHeader--Table'
                : settings.mode === 'grid' && settings.gridMode === 'masonry'
                  ? 'WidgetHeader WidgetHeader--Grid WidgetHeader--Grid--Masonry'
                  : settings.mode === 'grid'
                    ? 'WidgetHeader WidgetHeader--Grid'
                    : 'WidgetHeader WidgetHeader--Events'
            }
          >
            {settings.headline ? (
              <div className="WidgetHeaderHeadline">
                <h1>{settings.headline}</h1>
              </div>
            ) : null}
            {settings.search ? (
              <div className="Search">
                <DebouncedInput
                  className="SearchInput"
                  type="text"
                  placeholder={getLabel('search', settings)}
                  onChange={(value) => setSearchTerm(String(value))}
                  value={searchTerm}
                />
              </div>
            ) : null}
            {filterFields.map((field) => {
              const column = table.getColumn(field.id)!;
              const selected = (column.getFilterValue() as string | undefined) ?? '';

              return (
                <div className="Filter" key={field.id}>
                  <select
                    className="FilterSelect"
                    aria-label={field.label}
                    value={selected}
                    onChange={(e) => column.setFilterValue(e.target.value)}
                  >
                    <option value="">{field.label}</option>
                    {getFilterOptions(field, column.getFacetedRowModel().rows, selected).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
            {settings.mode === 'events' && eventsDateColumn ? (
              <div className="Event">
                <select
                  className="EventSelect"
                  value={eventsDateColumn.getFilterValue() as 'upcoming' | 'archive'}
                  onChange={(e) => {
                    eventsDateColumn.setFilterValue(e.target.value);

                    table.setSorting([
                      {
                        id: settings.eventsItemDateColumn!,
                        desc: e.target.value === 'upcoming' ? false : true,
                      },
                    ]);
                  }}
                >
                  <option value="upcoming">{getLabel('upcoming', settings)}</option>
                  <option value="archive">{getLabel('archive', settings)}</option>
                </select>
              </div>
            ) : null}
          </div>
        ) : null}

        {settings.mode === 'table' ? (
          <Table
            noBoardItems={noBoardItems}
            boardInfo={boardInfo}
            settings={settings}
            columns={columns}
            table={table}
            mode={mode}
            widgetId={widgetId}
            assetsById={assetsById}
            columnSettings={{}}
            previewTumbnailGeneratorUrl={previewTumbnailGeneratorUrl}
          />
        ) : null}
        {settings.mode === 'grid' ? (
          <Grid
            noBoardItems={noBoardItems}
            mode={mode}
            previewTumbnailGeneratorUrl={previewTumbnailGeneratorUrl}
            settings={settings}
            columns={columns}
            table={table}
            assetsById={assetsById}
          />
        ) : null}
        {settings.mode === 'events' ? (
          <Events
            noBoardItems={noBoardItems}
            mode={mode}
            previewTumbnailGeneratorUrl={previewTumbnailGeneratorUrl}
            settings={settings}
            columns={columns}
            table={table}
            assetsById={assetsById}
          />
        ) : null}

        {settings.mode === 'table' ? (
          <div className="WidgetFooter">
            <div className="PaginationWrapper">
              <div className="Items">
                <select
                  className="ItemsSelect"
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                >
                  {[5, 10, 25, 50, 100, 250, 500].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
              </div>
              {pageCount > 1 ? (
                <div className="Pagination">
                  <button className="PaginationButton" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                    <i className="fa-regular fa-angle-left"></i>
                  </button>
                  <Pagination currentPage={currentPage} pageCount={pageCount} onPageClick={(pageIndex) => table.setPageIndex(pageIndex)} />
                  <button className="PaginationButton" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                    <i className="fa-regular fa-angle-right"></i>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {settings.mode === 'grid' && table.getCanNextPage() ? (
          <div className="WidgetFooter WidgetFooter--Grid">
            <div
              className="LoadMoreButton"
              onClick={() => {
                table.setPageSize(table.getState().pagination.pageSize + (settings.gridItemsPerPage || GRID_ITEMS_PER_LOAD));
              }}
            >
              {getLabel('load_more', settings)}
            </div>
          </div>
        ) : null}
        {settings.mode === 'events' && table.getCanNextPage() ? (
          <div className="WidgetFooter WidgetFooter--Events">
            <div
              className="LoadMoreButton"
              onClick={() => {
                table.setPageSize(table.getState().pagination.pageSize + EVENTS_ITEMS_PER_LOAD);
              }}
            >
              {getLabel('load_more', settings)}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
